/**
 * Scramble team handicap allowance.
 *
 * WHS Appendix C ("Handicap Allowances"), published in Norwegian as NGF's
 * Vedlegg C (Handicapreglene 2024, s. 76). Order the roster by course handicap
 * ascending, then weight each position:
 *
 *   2 players: 35 / 15
 *   3 players: 30 / 20 / 10
 *   4 players: 25 / 20 / 15 / 10
 *
 * Appendix C's wording is "from the lowest to highest handicap": the largest
 * share goes to whoever receives fewest strokes. The single-player row below is
 * POLF's own — Appendix C starts at two — and gives a lone player their full
 * course handicap.
 *
 * Takes each player's course handicap — their strokes for the round from the
 * tee they actually play. Ordering by that rather than by handicap index
 * matters whenever partners play different tees: the lower index is not
 * necessarily the lower course handicap, and the top weight belongs to
 * whoever receives fewest strokes. On a single tee the two orderings always
 * agree, since course handicap is monotonic in index for a fixed tee.
 *
 * A course handicap, NOT a playing handicap: these weights are themselves the
 * handicap allowance for the format, so an event-level allowance must not be
 * applied before them. Two allowances multiplied make a committee's 50 % into
 * 42,5 % at an event allowance of 85 % (POL-76).
 *
 * Returns null for a roster size with no weighting rule, so callers can render
 * "not yet computable" rather than a misleading number.
 */
export const SCRAMBLE_WEIGHTS: Record<number, number[]> = {
  1: [1],
  2: [0.35, 0.15],
  3: [0.3, 0.2, 0.1],
  4: [0.25, 0.2, 0.15, 0.1],
};

export const getScrambleTeamStrokes = (
  courseHandicaps: readonly number[],
): number | null => {
  const weights = SCRAMBLE_WEIGHTS[courseHandicaps.length];
  if (!weights) return null;
  // Copy before sorting: on the API side this derives from a loaded TypeORM
  // relation, and sorting in place reorders the roster for every other reader.
  const sorted = [...courseHandicaps].sort((a, b) => a - b);
  return Math.round(sorted.reduce((acc, ch, i) => acc + ch * weights[i], 0));
};
